import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trophy,
  Users,
  Calendar,
  Clock,
  MapPin,
  ChevronRight,
  Camera,
  Star,
  Award,
  Newspaper,
  Image,
  TrendingUp,
  UserCheck,
  Sun,
  CloudSun,
  Flag,
  Medal,
  Play,
  MessageCircle,
  BookOpen,
  HelpCircle
} from 'lucide-react';
import { tournamentAPI } from '../services/api';
import './HomePage.css';


const HomePage = () => {
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        const response = await tournamentAPI.getAll();
        const now = new Date();

        // Filter upcoming tournaments
        const upcoming = response.data
          .filter(t => new Date(t.date) >= now && t.status !== 'cancelled')
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setUpcomingTournaments(upcoming);

        // Filter recent/past tournaments for results
        const recent = response.data
          .filter(t => new Date(t.date) < now)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 4);
        setRecentTournaments(recent);
      } catch (error) {
        console.error('Error loading tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTournaments();
  }, []);

  const formatDate = (dateString) => {
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatShortDate = (dateString) => {
    const options = { month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getDaysUntil = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Placeholder club news - will be dynamic later
  const clubNews = [
    {
      id: 1,
      title: "2024 Club Championship Results",
      date: "Dec 15, 2024",
      excerpt: "Congratulations to our 2024 Club Champion! See the full leaderboard and highlights from an exciting final round.",
      image: null,
      tag: "Results"
    },
    {
      id: 2,
      title: "New Pace of Play Guidelines",
      date: "Dec 10, 2024",
      excerpt: "Starting January 2025, we're implementing new pace of play tracking to improve the experience for all members.",
      image: null,
      tag: "Announcement"
    },
    {
      id: 3,
      title: "Holiday Tournament Schedule",
      date: "Dec 5, 2024",
      excerpt: "Check out our special holiday tournament schedule including the annual Turkey Shoot and New Year's Scramble.",
      image: null,
      tag: "Schedule"
    }
  ];

  // Placeholder leaderboard - will be dynamic later
  const seasonLeaders = [
    { rank: 1, name: "John Smith", points: 245, trend: "up" },
    { rank: 2, name: "Mike Johnson", points: 232, trend: "up" },
    { rank: 3, name: "David Williams", points: 218, trend: "down" },
    { rank: 4, name: "Robert Brown", points: 205, trend: "same" },
    { rank: 5, name: "James Davis", points: 198, trend: "up" }
  ];

  // Placeholder photo gallery - will be dynamic later
  const recentPhotos = [
    { id: 1, title: "Club Championship - Hole 18", placeholder: true },
    { id: 2, title: "Summer Scramble Winners", placeholder: true },
    { id: 3, title: "Tournament of Champions", placeholder: true },
    { id: 4, title: "Annual Member Dinner", placeholder: true },
    { id: 5, title: "Junior Golf Day", placeholder: true },
    { id: 6, title: "Closest to Pin Contest", placeholder: true }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <Badge variant="secondary" className="hero-badge-text">
              <Sun className="w-4 h-4 mr-1" />
              Balboa Park Golf Course
            </Badge>
          </div>
          <h1 className="hero-title">
            Balboa Men's Club
          </h1>
          <p className="hero-tagline">Wednesday Member Portal</p>
          <p className="hero-subtitle">
            San Diego's premier Wednesday golf league. Join us for weekly tournaments,
            friendly competition, and the best camaraderie in golf.
          </p>
          <div className="hero-actions">
            <Link to="/login">
              <Button size="lg" className="hero-button-primary">
                Member Sign In
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-number">500+</span>
              <span className="hero-stat-label">Active Members</span>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <span className="hero-stat-number">52</span>
              <span className="hero-stat-label">Events/Year</span>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <span className="hero-stat-number">Est. 1998</span>
              <span className="hero-stat-label">25+ Years</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="quick-info-bar">
        <div className="info-bar-container">
          <div className="info-item">
            <Calendar className="info-icon" />
            <span>Play Every Wednesday</span>
          </div>
          <div className="info-divider"></div>
          <div className="info-item">
            <Clock className="info-icon" />
            <span>First Tee: 7:00 AM</span>
          </div>
          <div className="info-divider"></div>
          <div className="info-item">
            <MapPin className="info-icon" />
            <span>Balboa Park Golf Course</span>
          </div>
          <div className="info-divider"></div>
          <div className="info-item">
            <CloudSun className="info-icon" />
            <span>San Diego, CA</span>
          </div>
        </div>
      </section>

      {/* Ask Balboney Section */}
      <section className="balboney-section">
        <div className="section-container">
          <div className="balboney-card">
            <div className="balboney-icon-container">
              <MessageCircle className="balboney-icon" />
            </div>
            <div className="balboney-content">
              <h2 className="balboney-title">
                <span className="balboney-name">Ask Balboney</span>
                <Badge variant="secondary" className="balboney-badge">AI Assistant</Badge>
              </h2>
              <p className="balboney-description">
                Meet Balboney, your AI golf expert! Trained on the USGA Rules of Golf,
                Balboa Park local rules, hole-by-hole hazards, course tips, and club policies.
                Get instant answers to your rules questions anytime.
              </p>
              <div className="balboney-features">
                <div className="balboney-feature">
                  <BookOpen className="feature-icon" />
                  <span>USGA Rules</span>
                </div>
                <div className="balboney-feature">
                  <Flag className="feature-icon" />
                  <span>Local Rules</span>
                </div>
                <div className="balboney-feature">
                  <HelpCircle className="feature-icon" />
                  <span>Course Tips</span>
                </div>
              </div>
              <a href="https://balboney.sandiego.golf" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="balboney-button"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Chat with Balboney
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Tournaments Section */}
      <section className="tournaments-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              <Calendar className="section-icon" />
              Upcoming Tournaments
            </h2>
            <p className="section-description">
              Sign up for our upcoming Wednesday events
            </p>
          </div>

          {loading ? (
            <div className="tournaments-loading">
              <div className="loading-spinner"></div>
              <p>Loading tournaments...</p>
            </div>
          ) : upcomingTournaments.length > 0 ? (
            <div className="tournaments-grid">
              {upcomingTournaments.map((tournament) => {
                const daysUntil = getDaysUntil(tournament.date);
                return (
                  <Card key={tournament._id} className="tournament-card">
                    <CardHeader>
                      <div className="tournament-card-header">
                        <div className="tournament-date-badge">
                          {daysUntil === 0 ? (
                            <Badge variant="success">Today</Badge>
                          ) : daysUntil === 1 ? (
                            <Badge variant="warning">Tomorrow</Badge>
                          ) : daysUntil <= 7 ? (
                            <Badge variant="info">{daysUntil} days</Badge>
                          ) : (
                            <Badge variant="secondary">{daysUntil} days</Badge>
                          )}
                        </div>
                        <Trophy className="tournament-icon" />
                      </div>
                      <CardTitle className="tournament-name">{tournament.name}</CardTitle>
                      <CardDescription>
                        <div className="tournament-details">
                          <div className="tournament-detail">
                            <Calendar className="detail-icon" />
                            <span>{formatDate(tournament.date)}</span>
                          </div>
                          {tournament.startTime && (
                            <div className="tournament-detail">
                              <Clock className="detail-icon" />
                              <span>First tee: {tournament.startTime}</span>
                            </div>
                          )}
                        </div>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="tournament-stats">
                        {tournament.players && (
                          <div className="tournament-stat">
                            <Users className="stat-icon" />
                            <span>{tournament.players.length} Signed Up</span>
                          </div>
                        )}
                        {tournament.format && (
                          <div className="tournament-stat">
                            <Flag className="stat-icon" />
                            <span>{tournament.format}</span>
                          </div>
                        )}
                      </div>
                      {tournament.teeSheetPublished && (
                        <Link
                          to={`/tournament/${tournament._id}/teesheet/view`}
                          className="view-teesheet-link"
                        >
                          <Button variant="outline" size="sm" className="w-full mt-3">
                            View Tee Sheet
                            <ChevronRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="no-tournaments">
              <Trophy className="no-tournaments-icon" />
              <p>No upcoming tournaments scheduled</p>
              <span>Check back soon for new events</span>
            </div>
          )}
        </div>
      </section>

      {/* Recent Results & Leaderboard Split Section */}
      <section className="results-leaderboard-section">
        <div className="section-container">
          <div className="split-grid">
            {/* Recent Tournament Results */}
            <div className="results-column">
              <div className="column-header">
                <h3 className="column-title">
                  <Award className="column-icon" />
                  Recent Results
                </h3>
                <Link to="/login" className="view-all-link">View All</Link>
              </div>
              <div className="results-list">
                {recentTournaments.length > 0 ? (
                  recentTournaments.map((tournament) => (
                    <div key={tournament._id} className="result-item">
                      <div className="result-date">
                        <span className="result-month">{formatShortDate(tournament.date).split(' ')[0]}</span>
                        <span className="result-day">{formatShortDate(tournament.date).split(' ')[1]}</span>
                      </div>
                      <div className="result-info">
                        <h4 className="result-name">{tournament.name}</h4>
                        <p className="result-winner">
                          {tournament.winner ? `Winner: ${tournament.winner}` : 'Results pending'}
                        </p>
                      </div>
                      <ChevronRight className="result-arrow" />
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <p>No recent tournament results</p>
                  </div>
                )}
                {recentTournaments.length === 0 && (
                  <>
                    {/* Placeholder results */}
                    <div className="result-item placeholder">
                      <div className="result-date">
                        <span className="result-month">Dec</span>
                        <span className="result-day">18</span>
                      </div>
                      <div className="result-info">
                        <h4 className="result-name">Winter Championship</h4>
                        <p className="result-winner">Coming soon</p>
                      </div>
                      <ChevronRight className="result-arrow" />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Season Leaderboard */}
            <div className="leaderboard-column">
              <div className="column-header">
                <h3 className="column-title">
                  <TrendingUp className="column-icon" />
                  Season Standings
                </h3>
                <Link to="/login" className="view-all-link">Full Leaderboard</Link>
              </div>
              <div className="leaderboard-list">
                {seasonLeaders.map((player) => (
                  <div key={player.rank} className={`leaderboard-item rank-${player.rank}`}>
                    <div className="leaderboard-rank">
                      {player.rank <= 3 ? (
                        <Medal className={`medal-icon medal-${player.rank}`} />
                      ) : (
                        <span className="rank-number">{player.rank}</span>
                      )}
                    </div>
                    <div className="leaderboard-player">
                      <span className="player-name">{player.name}</span>
                    </div>
                    <div className="leaderboard-points">
                      <span className="points-value">{player.points}</span>
                      <span className="points-label">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="gallery-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              <Camera className="section-icon" />
              Photo Gallery
            </h2>
            <p className="section-description">
              Memories from our tournaments and events
            </p>
          </div>

          <div className="gallery-grid">
            {recentPhotos.map((photo) => (
              <div key={photo.id} className="gallery-item">
                {photo.placeholder ? (
                  <div className="gallery-placeholder">
                    <Image className="placeholder-icon" />
                    <span className="placeholder-text">Photo Coming Soon</span>
                  </div>
                ) : (
                  <img src={photo.url} alt={photo.title} className="gallery-image" />
                )}
                <div className="gallery-overlay">
                  <span className="gallery-title">{photo.title}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="gallery-cta">
            <Button variant="outline" size="lg">
              <Play className="mr-2 h-4 w-4" />
              View Full Gallery
            </Button>
          </div>
        </div>
      </section>

      {/* Club News Section */}
      <section className="news-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              <Newspaper className="section-icon" />
              Club News & Announcements
            </h2>
            <p className="section-description">
              Stay updated with the latest from the club
            </p>
          </div>

          <div className="news-grid">
            {clubNews.map((news) => (
              <Card key={news.id} className="news-card">
                <CardContent className="news-content">
                  <div className="news-header">
                    <Badge variant="outline" className="news-tag">{news.tag}</Badge>
                    <span className="news-date">{news.date}</span>
                  </div>
                  <h3 className="news-title">{news.title}</h3>
                  <p className="news-excerpt">{news.excerpt}</p>
                  <Button variant="ghost" size="sm" className="news-read-more">
                    Read More <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Member Spotlight Section */}
      <section className="spotlight-section">
        <div className="section-container">
          <div className="spotlight-card">
            <div className="spotlight-content">
              <Badge className="spotlight-badge">
                <Star className="w-3 h-3 mr-1" />
                Member Spotlight
              </Badge>
              <h2 className="spotlight-title">Player of the Month</h2>
              <p className="spotlight-description">
                Recognizing outstanding performance, sportsmanship, and contribution to our club community.
                Check back soon to see this month's featured member!
              </p>
              <div className="spotlight-placeholder">
                <Users className="spotlight-icon" />
                <span>Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Tee It Up?</h2>
            <p className="cta-description">
              Sign in to view your tee times, check tournament results, manage your wallet,
              and stay connected with the Balboa Men's Club community.
            </p>
            <div className="cta-actions">
              <Link to="/login">
                <Button size="lg" variant="secondary" className="cta-button">
                  <UserCheck className="mr-2 h-5 w-5" />
                  Member Sign In
                </Button>
              </Link>
            </div>
            <p className="cta-subtext">
              Not a member? Contact the pro shop to learn about joining our Wednesday league.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <Trophy className="footer-logo" />
              <div className="footer-brand-text">
                <span className="footer-name">Balboa Men's Club</span>
                <span className="footer-tagline">Wednesday Member Portal</span>
              </div>
            </div>
            <div className="footer-links">
              <div className="footer-link-group">
                <h4>Quick Links</h4>
                <Link to="/login">Member Sign In</Link>
                <a href="https://balboaparkgolf.com" target="_blank" rel="noopener noreferrer">Course Website</a>
                <a href="tel:+16192395660">Pro Shop: (619) 239-5660</a>
              </div>
              <div className="footer-link-group">
                <h4>Location</h4>
                <p>Balboa Park Golf Course</p>
                <p>2600 Golf Course Dr</p>
                <p>San Diego, CA 92102</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Balboa Men's Club Wednesday League. All rights reserved.
            </p>
            <p className="footer-powered">
              Powered by Pards Tee Maker
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;
